import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { MacroDefinitionFilter, PackageResourceType } from '@/__generated__/graphql';
import {
  ALL_PACKAGES_VALUE,
  PACKAGE_NAME_PARAM,
  PackageFilterDropdown,
} from '@/components/ModelFilters/PackageFilterDropdown';
import { PageHeading } from '@/components/PageHeading';
import { getNameLinkCell } from '@/components/Table/LinkCell';
import { TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useLinkToExplorer } from '@/dbt-explorer';
import { useDiscoveryDefinitionMacros } from '@/hooks/discovery/definitionMacros';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type TableData = {
  name: string;
  uniqueId: string;
  description: string | null;
  macroArguments: string | undefined;
};

type MacroDefinitionListNode = {
  uniqueId: string;
  description: string | null;
  arguments: ({ name: string | null } | null)[] | null;
};

const getTableData = (
  macro: MacroDefinitionListNode | undefined | null,
): TableData | undefined => {
  if (!macro) return undefined;
  const uniqueId = parseUniqueId(macro.uniqueId);
  const macroArguments = macro.arguments
    ?.flatMap((node) => {
      const name = node?.name;
      return name ? [name] : [];
    })
    .join(', ');
  return {
    name: uniqueId.remainder,
    uniqueId: uniqueId.raw,
    description: macro.description,
    macroArguments,
  };
};

export const MacroFilterView = () => {
  const pageSize = INITIAL_PAGE_SIZE;
  const { environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const projectScope = useUrlProjectParams();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const [searchParams] = useSearchParams();
  const [packageInfoLoaded, setPackageInfoLoaded] = useState(false);

  const filter = useMemo((): Partial<MacroDefinitionFilter> => {
    const qsPackageName = searchParams.get(PACKAGE_NAME_PARAM) ?? undefined;
    return {
      packageName: qsPackageName === ALL_PACKAGES_VALUE ? undefined : qsPackageName,
    };
  }, [searchParams]);

  const paginatedMacroQuery = useDiscoveryDefinitionMacros({
    environmentId,
    userId: userId as number | null,
    first: pageSize,
    filter,
    pause: !packageInfoLoaded || userId === 'loading',
  });

  const macros = paginatedMacroQuery.data;
  const pageInfo = paginatedMacroQuery.pageInfo;

  const data = useMemo((): any[] | undefined => {
    return macros?.map(getTableData);
  }, [macros]);

  if (paginatedMacroQuery.error) return <>{paginatedMacroQuery.error.stack}</>;
  const isLoadingMore = paginatedMacroQuery.fetching;
  const isInitialLoad =
    !packageInfoLoaded ||
    (paginatedMacroQuery.fetching && paginatedMacroQuery.data === undefined);

  return (
    <>
      <PageHeading className="mb-10">Macros</PageHeading>
      <div className="my-2 flex flex-row gap-2">
        <PackageFilterDropdown
          environmentId={environmentId}
          onLoad={() => setPackageInfoLoaded(true)}
          resourceType={PackageResourceType.Macro}
        />
      </div>
      <ResourceFilterTable
        pluralizedResultName="macros"
        data={data}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            id: 'name',
            size: 60,
            minSize: 240,
            cell: getNameLinkCell(getDetailsPageLink, projectScope),
          },
          {
            header: 'Arguments',
            accessorKey: 'macroArguments',
            cell: TruncatedCell,
          },
          {
            header: 'Description',
            accessorKey: 'description',
            cell: TruncatedCell,
          },
        ]}
        pageInfo={pageInfo}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        pageSize={pageSize}
        fetchMore={paginatedMacroQuery.fetchMore}
      />
    </>
  );
};
